import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Enlace, irA, useRuta } from '../../router';
import { useSesion } from '../../session/Sesion';
import { Avatar } from '../Avatar';
import { Icono } from '../Icono';
import { Insignia } from '../Insignia';
import { Logo } from './Logo';
import type { EntradaMenu, ItemMenu } from './menus';

const ContextoMenu = createContext<(() => void) | null>(null);

// Gana el ítem cuya ruta coincide con más caracteres: así '/admin/reclamos' no
// deja también encendido a '/admin'.
function itemActivo(menu: readonly EntradaMenu[], camino: string): ItemMenu | undefined {
  let mejor: ItemMenu | undefined;
  let largo = -1;
  for (const entrada of menu) {
    if (entrada === 'separador') continue;
    const candidatas = [entrada.ruta, ...(entrada.tambien ?? [])];
    for (const r of candidatas) {
      const coincide = r.endsWith('/') ? camino.startsWith(r) : camino === r;
      if (coincide && r.length > largo) {
        mejor = entrada;
        largo = r.length;
      }
    }
  }
  return mejor;
}

interface PropsPaginaPanel {
  menu: readonly EntradaMenu[];
  /** A dónde lleva el logo. */
  inicio: string;
  distintivo?: string;
  children: ReactNode;
}

export function PaginaPanel({ menu, inicio, distintivo, children }: PropsPaginaPanel) {
  const { camino } = useRuta();
  const { usuario, salir } = useSesion();
  const [menuAbierto, setMenuAbierto] = useState(false);
  const activo = itemActivo(menu, camino);
  const nombre = usuario ? `${usuario.nombre} ${usuario.apellido}` : 'MedChoice';

  useEffect(() => { setMenuAbierto(false); }, [camino]);

  useEffect(() => {
    if (!menuAbierto) return undefined;
    const alTeclear = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuAbierto(false); };
    window.addEventListener('keydown', alTeclear);
    return () => { window.removeEventListener('keydown', alTeclear); };
  }, [menuAbierto]);

  return (
    <div className={menuAbierto ? 'panel panel--menu-abierto' : 'panel'}>
      <aside className="panel__lateral" id="menu-panel">
        <div className="panel__marca">
          <Logo tamano={30} a={inicio} />
          {distintivo && <Insignia tono="admin" tamano="chico">{distintivo}</Insignia>}
        </div>

        <nav className="panel__nav" aria-label="Secciones">
          {menu.map((entrada, i) => {
            if (entrada === 'separador') return <hr key={`separador-${i}`} className="panel__separador" />;
            const esActivo = entrada === activo;
            return (
              <Enlace
                key={entrada.ruta}
                a={entrada.ruta}
                className={esActivo ? 'panel__item panel__item--activo' : 'panel__item'}
                aria-current={esActivo ? 'page' : undefined}
              >
                <Icono nombre={entrada.icono} tamano={18} />
                <span className="panel__item-texto">{entrada.texto}</span>
                {entrada.insignia ? <Insignia tono="coral" tamano="chico">{entrada.insignia}</Insignia> : null}
              </Enlace>
            );
          })}
        </nav>

        <div className="panel__usuario">
          <Avatar nombre={nombre} tamano={34} decorativo />
          <div className="panel__usuario-datos">
            <div className="panel__usuario-nombre">{nombre}</div>
            <div className="panel__usuario-email">{usuario?.email}</div>
          </div>
          <button
            type="button"
            className="boton-sutil"
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
            onClick={() => { void salir().finally(() => { irA('/login'); }); }}
          >
            <Icono nombre="log-out" tamano={16} />
          </button>
        </div>
      </aside>

      {menuAbierto && <div className="panel__velo" onClick={() => { setMenuAbierto(false); }} aria-hidden="true" />}

      <ContextoMenu.Provider value={() => { setMenuAbierto(true); }}>
        <div className="panel__columna">{children}</div>
      </ContextoMenu.Provider>
    </div>
  );
}

interface PropsSeccionPanel {
  titulo: string;
  descripcion?: ReactNode;
  acciones?: ReactNode;
  children: ReactNode;
}

// Cada pantalla del panel arma su propia barra superior: el título y las acciones
// son de la pantalla, no del diseño que la envuelve.
export function SeccionPanel({ titulo, descripcion, acciones, children }: PropsSeccionPanel) {
  const abrirMenu = useContext(ContextoMenu);
  return (
    <>
      <div className="panel__barra">
        {abrirMenu && (
          <button
            type="button"
            className="boton-sutil panel__abrir-menu"
            onClick={abrirMenu}
            aria-label="Abrir menú"
            aria-controls="menu-panel"
          >
            <Icono nombre="menu" tamano={22} />
          </button>
        )}
        <div className="panel__titulos">
          <h1 className="panel__titulo">{titulo}</h1>
          {descripcion && <div className="panel__descripcion">{descripcion}</div>}
        </div>
        {acciones && <div className="panel__acciones">{acciones}</div>}
      </div>
      <main id="contenido" className="panel__contenido">{children}</main>
    </>
  );
}
